import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, MessageCircle, Star, BadgeCheck, ChevronLeft, ChevronRight, Truck, Shield, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProductById, getProductImages, productCategories, PLACEHOLDER_IMAGE } from '../data/products';

const PHONE_NUMBER = "+919971231812";
const WHATSAPP_NUMBER = "919971231812";

// Image Gallery Component
const ImageGallery = ({ images, productName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadErrors, setLoadErrors] = useState({});

  const handleError = (index) => {
    setLoadErrors(prev => ({ ...prev, [index]: true }));
  };

  const getUrl = (index) => {
    return loadErrors[index] ? PLACEHOLDER_IMAGE : (images[index] || PLACEHOLDER_IMAGE);
  };

  const goNext = () => setSelectedIndex((prev) => (prev + 1) % images.length);
  const goPrev = () => setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="space-y-4">
      <div className="relative rounded-2xl overflow-hidden bg-white shadow-lg aspect-square">
        <img
          src={getUrl(selectedIndex)}
          alt={productName}
          className="w-full h-full object-cover"
          onError={() => handleError(selectedIndex)}
          data-testid="main-product-image"
        />
        
        {images.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
              data-testid="prev-image-btn"
            >
              <ChevronLeft className="w-6 h-6 text-stone-700" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
              data-testid="next-image-btn"
            >
              <ChevronRight className="w-6 h-6 text-stone-700" />
            </button>
            <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
              {selectedIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="thumbnail-gallery">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                selectedIndex === idx 
                  ? 'border-teal-500 shadow-md' 
                  : 'border-transparent opacity-70 hover:opacity-100'
              }`}
              data-testid={`thumbnail-${idx}`}
            >
              <img
                src={loadErrors[idx] ? PLACEHOLDER_IMAGE : img}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={() => handleError(idx)}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Feature List Component
const FeatureList = ({ features }) => {
  if (!features || features.length === 0) return null;
  
  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-stone-800">Key Features:</h3>
      <div className="grid grid-cols-2 gap-3">
        {features.map((feature, idx) => (
          <div key={idx} className="flex items-center gap-2 text-stone-600">
            <BadgeCheck className="w-5 h-5 text-teal-600 flex-shrink-0" />
            <span>{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const found = getProductById(productId);
    setProduct(found);
    setLoading(false);
  }, [productId]);

  const productImages = product ? getProductImages(product) : [PLACEHOLDER_IMAGE];
  
  const getWhatsAppLink = () => {
    const msg = encodeURIComponent(
      `Hi! I'm interested in ${product?.name} (${product?.brand}). Can you share details and pricing?`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
  };

  if (loading) {
    return (
      <main className="bg-[#FDFBF7] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-stone-200 rounded w-32 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="h-96 bg-stone-200 rounded-2xl"></div>
              <div className="space-y-4">
                <div className="h-8 bg-stone-200 rounded w-3/4"></div>
                <div className="h-6 bg-stone-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="bg-[#FDFBF7] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-20">
            <h1 className="text-3xl font-bold text-stone-800 mb-4">Product Not Found</h1>
            <p className="text-stone-600 mb-8">The product you're looking for doesn't exist.</p>
            <Button 
              onClick={() => navigate('/products')}
              className="rounded-full bg-teal-600 hover:bg-teal-700"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Products
            </Button>
          </div>
        </div>
      </main>
    );
  }

  const categoryInfo = productCategories[product.category];

  return (
    <main data-testid="product-detail-page" className="bg-[#FDFBF7] min-h-screen">
      <div className="bg-gradient-to-br from-[#FDFBF7] via-[#F0FDFA] to-[#CCFBF1] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm flex-wrap">
            <Link to="/" className="text-stone-500 hover:text-teal-600">Home</Link>
            <span className="text-stone-400">/</span>
            <Link to="/products" className="text-stone-500 hover:text-teal-600">Products</Link>
            <span className="text-stone-400">/</span>
            <span className="text-stone-800 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/products')}
            className="flex items-center gap-2 text-stone-600 hover:text-teal-600 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Products</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              {product.bestseller && (
                <div className="mb-4">
                  <span className="bg-amber-500 text-white text-sm font-semibold px-4 py-2 rounded-full">
                    ⭐ Bestseller
                  </span>
                </div>
              )}
              <ImageGallery images={productImages} productName={product.name} />
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="bg-teal-100 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                  {product.brand}
                </span>
                <span className="bg-stone-100 text-stone-600 text-sm font-medium px-3 py-1 rounded-full">
                  {product.type}
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-stone-800">
                {product.name}
              </h1>

              <div className="flex items-center gap-2">
                <div className="flex">
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                </div>
                <span className="text-stone-500 text-sm">Trusted by 1000+ customers</span>
              </div>

              <p className="text-stone-600 text-lg leading-relaxed">
                {product.description}
              </p>

              <FeatureList features={product.features} />

              <div className="flex flex-wrap gap-4 py-4 border-y border-stone-200">
                <div className="flex items-center gap-2 text-stone-600">
                  <Shield className="w-5 h-5 text-teal-600" />
                  <span className="text-sm">100% Genuine</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600">
                  <Truck className="w-5 h-5 text-teal-600" />
                  <span className="text-sm">Free Delivery in Delhi NCR</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600">
                  <Award className="w-5 h-5 text-teal-600" />
                  <span className="text-sm">Brand Warranty</span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                <p className="text-stone-700 font-medium">
                  💰 Contact us for the best prices! We offer competitive rates and special discounts.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${PHONE_NUMBER}`} className="flex-1">
                  <Button 
                    size="lg"
                    className="w-full rounded-full bg-teal-600 hover:bg-teal-700"
                    data-testid="product-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
                <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button 
                    size="lg"
                    className="w-full rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
                    data-testid="product-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp Inquiry
                  </Button>
                </a>
              </div>

              <div className="bg-stone-50 rounded-xl p-4 space-y-2">
                <p className="font-semibold text-stone-800">Visit Our Store</p>
                <p className="text-stone-600 text-sm">
                  Shop No. 72/5, Yusuf Sarai Market, New Delhi, Delhi 110016
                </p>
                <p className="text-stone-600 text-sm">
                  Open Daily: 10:30 AM - 9:30 PM
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gradient-to-br from-[#F0FDFA] to-[#CCFBF1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
            Looking for More {categoryInfo?.name}?
          </h2>
          <p className="text-stone-600 mb-6">
            We have {categoryInfo?.count}+ products in this category from brands like {categoryInfo?.brands?.join(', ')}.
          </p>
          <Link to="/products">
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              Browse All {categoryInfo?.name}
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ProductDetailPage;
